import { useLoyaltyTierCopy } from "../../../../services/hooks/loyalty/useLoyaltyTierCopy";
import { useTranslationI18 } from "../../../../services/hooks/useTranslationI18";
import { localizedNumber } from "../../../../utils/help/localization/localization";
import {
  getTierColor,
  getTierRank,
  toTierCssProperties,
} from "../../../../utils/loyalty/tiers";
import {
  LoyaltyTierProgress,
  LoyaltyTierSystemTier,
} from "../../../../utils/loyaltyTypes";
import ConfettiComponent from "../../../global/AnimatedConffeti/AnimatedConfettiComponent";
import { LoyaltyPointsIcon } from "../../../global/Icons/LoyaltyPointsIcon";
import { LockIcon } from "../../../global/Icons/loyalty.icons";
import { LoyaltyTierBadge } from "../Badge/LoyaltyTierBadge";
import { LoyaltyTierRing } from "../Ring/LoyaltyTierRing";
import styles from "./LoyaltyTierCard.module.scss";

type LoyaltyTierCardVariant = "current" | "preview" | "detailed";

interface LoyaltyTierCardProps {
  tier: LoyaltyTierSystemTier;
  /** Tiers sorted by `tierOrder`; drives badge rank and default colours. */
  tiers: LoyaltyTierSystemTier[];
  variant: LoyaltyTierCardVariant;
  /** Present only on the member's own tier. */
  tierProgress?: LoyaltyTierProgress;
  /** Tier the member has not reached yet. */
  locked?: boolean;
  /** Tier the member has already moved past. */
  achieved?: boolean;
  showNextTierPill?: boolean;
  onShowTerms?: () => void;
}

export const LoyaltyTierCard = ({
  tier,
  tiers,
  variant,
  tierProgress,
  locked = false,
  achieved = false,
  showNextTierPill = false,
  onShowTerms,
}: LoyaltyTierCardProps) => {
  const { t } = useTranslationI18();
  const {
    getTierName,
    getUnitLabelForValue,
    getEarningRateText,
    getOutOfUnitsText,
    getCurrentTierRequirements,
  } = useLoyaltyTierCopy();
  const name = getTierName(tier);
  const color = getTierColor(tier, tiers);
  const rank = getTierRank(tier, tiers);
  const tierStyle = toTierCssProperties(color);
  const entryUnits = tier.minUnitsToEnter || 0;
  const entryUnitsText = localizedNumber(entryUnits);

  const confetti = (
    <span className={styles.card_confetti} aria-hidden="true">
      <ConfettiComponent contained color={color} />
    </span>
  );

  if (variant === "current") {
    const nextTier = tiers.find(
      (candidate) => candidate.tierOrder > tier.tierOrder
    );
    // loyalty-service derives both remainders from the same qualification
    // balance (`minToStay - balance` and `nextTier.minToEnter - balance`, each
    // floored at 0), so the balance can be recovered from either one. Taking the
    // larger recovery keeps the ring correct when one of them has been floored.
    const totalUnits = nextTier?.minUnitsToEnter || tier.minUnitsToStay || 0;
    const collectedUnits = tierProgress
      ? Math.max(
          (tier.minUnitsToStay || 0) - tierProgress.remainingUnitsToStay,
          tierProgress.remainingUnitsToUpgrade === null
            ? 0
            : (nextTier?.minUnitsToEnter || 0) -
                tierProgress.remainingUnitsToUpgrade
        )
      : 0;
    const completedUnits = Math.min(totalUnits, Math.max(0, collectedUnits));

    return (
      <article
        className={`${styles.tier_card} ${styles.tier_card_current}`}
        style={tierStyle}
        data-testid={`loyalty-tier-${tier.tierId}`}
      >
        {confetti}
        <div className={styles.current_copy}>
          <h2>{t("pointing-system:tier-member", { tier: name })}</h2>
          <ul>
            {getCurrentTierRequirements(tier, tiers, tierProgress).map(
              (requirement) => (
                <li key={`${requirement.value}${requirement.suffix ?? ""}`}>
                  <LoyaltyPointsIcon />
                  <span>
                    <strong>{requirement.value}</strong>
                    {requirement.suffix ? ` ${requirement.suffix}` : ""}
                  </span>
                </li>
              )
            )}
          </ul>
        </div>
        <LoyaltyTierRing
          rank={rank}
          color={color}
          completedUnits={completedUnits}
          unitsLabel={getOutOfUnitsText(totalUnits)}
          progress={totalUnits ? completedUnits / totalUnits : 0}
        />
      </article>
    );
  }

  if (variant === "preview") {
    return (
      <article
        className={`${styles.tier_card} ${styles.tier_card_preview} ${
          achieved ? styles.tier_card_achieved : ""
        }`}
        style={tierStyle}
        data-testid={`loyalty-tier-${tier.tierId}`}
      >
        {confetti}
        <h2 className={styles.preview_title}>{name}</h2>
        <p className={styles.preview_rate}>{getEarningRateText(tier)}</p>
        {/* Figma runs the threshold and the Next Tier pill down one centred
            line, so they share a row rather than each being pinned to the card. */}
        <div className={styles.tier_footer}>
          <p className={styles.tier_units}>
            <strong>{entryUnitsText}</strong>
            <span>{getUnitLabelForValue(entryUnits)}</span>
            <LoyaltyPointsIcon />
          </p>
          {showNextTierPill && (
            <span className={styles.tier_pill}>
              {t("pointing-system:next-tier")}
            </span>
          )}
        </div>
        {locked && (
          <span className={styles.preview_lock}>
            <LockIcon />
          </span>
        )}
      </article>
    );
  }

  return (
    <article
      className={`${styles.tier_card} ${styles.tier_card_detailed}`}
      style={tierStyle}
      data-testid={`loyalty-tier-${tier.tierId}`}
    >
      {confetti}
      <h2 className={styles.detailed_title}>
        <LoyaltyTierBadge rank={rank} color={color} size="small" />
        {name}
      </h2>
      <p className={styles.detailed_rate}>{getEarningRateText(tier)}</p>
      <p className={styles.tier_units}>
        <strong>{entryUnitsText}</strong>
        <span>{getUnitLabelForValue(entryUnits)}</span>
        <LoyaltyPointsIcon />
      </p>
      {locked && (
        <span className={styles.detailed_lock}>
          <LockIcon />
        </span>
      )}
      {onShowTerms && (
        <button
          type="button"
          className={styles.tier_terms}
          onClick={onShowTerms}
          aria-label={t("pointing-system:view-terms-and-conditions")}
        >
          <span aria-hidden="true">i</span>
        </button>
      )}
    </article>
  );
};
